import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button, type ButtonVariant } from './Button';

/**
 * Shared confirm-dialog surface (UX-10). Replaces the hand-rolled modals
 * that used to live in `pages/epics/[id]/page.tsx` (epic + story actions),
 * `DangerZone` (project delete) and `SkillsManager` (skill delete), plus
 * the `window.confirm` in `pages/account/members.tsx`.
 *
 * Visual language matches the original epic `ConfirmDialog`:
 * `bg-slate-900/40 backdrop-blur-xs` backdrop, `rounded-xl shadow-card p-5
 * max-w-sm` card.
 */
export interface ConfirmDialogProps {
  open: boolean;
  title: React.ReactNode;
  description?: React.ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  tone: 'primary' | 'danger';
  /** While true: Cancel is disabled, Escape/backdrop-click are ignored,
   *  and the confirm button shows its loading spinner. */
  pending?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  /** Used to build the confirm button's `data-testid` (kept stable across
   *  the migration so existing call sites' test IDs still resolve). */
  testId?: string;
  /** Extra content rendered between the description and the action row —
   *  e.g. DangerZone's "type the project id to confirm" input. */
  children?: React.ReactNode;
  /** Disables the confirm button regardless of `pending` (e.g. until a
   *  typed confirmation matches). */
  confirmDisabled?: boolean;
}

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), ' +
  'input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  title,
  description,
  confirmLabel,
  cancelLabel = 'Cancel',
  tone,
  pending = false,
  onConfirm,
  onCancel,
  testId,
  children,
  confirmDisabled = false,
}) => {
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement | null>(null);

  const cancel = useCallback(() => {
    if (pending) return;
    onCancel();
  }, [pending, onCancel]);

  // Escape closes (unless pending) and Tab is trapped inside the dialog
  // while it's open.
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        cancel();
        return;
      }
      if (e.key !== 'Tab') return;
      const root = dialogRef.current;
      if (!root) return;
      const focusables = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, cancel]);

  if (!open) return null;

  const confirmVariant: ButtonVariant = tone === 'danger' ? 'danger' : 'primary';

  return createPortal(
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/40 backdrop-blur-xs"
      onClick={cancel}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="bg-white border border-slate-200 rounded-xl shadow-card p-5 max-w-sm w-full mx-4"
        onClick={(e) => e.stopPropagation()}
        data-testid={testId ? `${testId}-dialog` : undefined}
      >
        <h3 id={titleId} className="text-base font-semibold text-slate-900 mb-1">
          {title}
        </h3>
        {description && <p className="text-xs text-slate-600">{description}</p>}
        {children && <div className="mt-3">{children}</div>}
        <div className="mt-4 flex justify-end gap-2">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={cancel}
            disabled={pending}
            autoFocus
            data-dialog-cancel="true"
          >
            {cancelLabel}
          </Button>
          <Button
            type="button"
            variant={confirmVariant}
            size="sm"
            loading={pending}
            disabled={pending || confirmDisabled}
            onClick={onConfirm}
            data-testid={testId}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

// ---------------------------------------------------------------------------
// useConfirm — imperative helper for call sites that don't want to own a
// ConfirmDialog's open state themselves. Render the returned `dialog` node
// somewhere in the calling component's tree (it renders nothing when idle).
// ---------------------------------------------------------------------------

export interface ConfirmOptions {
  title: React.ReactNode;
  description?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: 'primary' | 'danger';
  testId?: string;
}

export interface UseConfirmResult {
  /** Resolves `true` when the operator confirms, `false` on cancel. */
  confirm: (opts: ConfirmOptions) => Promise<boolean>;
  /** Render this in the tree — it's `null` until `confirm()` is pending. */
  dialog: React.ReactNode;
}

export function useConfirm(): UseConfirmResult {
  const [pendingConfirm, setPendingConfirm] = useState<{
    opts: ConfirmOptions;
    resolve: (value: boolean) => void;
  } | null>(null);

  const confirm = useCallback((opts: ConfirmOptions): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      setPendingConfirm({ opts, resolve });
    });
  }, []);

  const settle = useCallback((value: boolean) => {
    setPendingConfirm((current) => {
      current?.resolve(value);
      return null;
    });
  }, []);

  const dialog = pendingConfirm ? (
    <ConfirmDialog
      open
      title={pendingConfirm.opts.title}
      description={pendingConfirm.opts.description}
      confirmLabel={pendingConfirm.opts.confirmLabel ?? 'Confirm'}
      cancelLabel={pendingConfirm.opts.cancelLabel}
      tone={pendingConfirm.opts.tone ?? 'primary'}
      testId={pendingConfirm.opts.testId}
      onConfirm={() => settle(true)}
      onCancel={() => settle(false)}
    />
  ) : null;

  return { confirm, dialog };
}

export default ConfirmDialog;
